import { format } from 'date-fns'

const BULK_SUFFIX = ' (bulk)'
const DIGITS_ONLY_REGEX = /^\d+$/
const LETTERS_ONLY_REGEX = /^[A-Z]+$/
const ALPHANUMERIC_REGEX = /^[A-Z0-9]+$/
const BULK_CODE_CHAR_LEN_MIN = 4
const BULK_CODE_CHAR_LEN_MAX = 8
const EMPTY_CODE_PLACEHOLDER = '—'

const employeeFullName = (emp: IDiscountCodeEmployee | null): string => {
	if (!emp) return EMPTY_CODE_PLACEHOLDER
	return `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim() || emp.email
}

export const resolveDiscountCodeLabel = (
	code: Pick<IDiscountCodeListModel, 'id' | 'code' | 'promotionName' | 'isBulk'>,
): string => {
	if (code.isBulk) {
		return code.promotionName || `#${code.id}`
	}
	if (code.code && code.code !== EMPTY_CODE_PLACEHOLDER) {
		return code.code
	}
	return code.promotionName || `#${code.id}`
}

export const DiscountCodeListItem = (json: IDiscountCodeListItem): IDiscountCodeListModel => ({
	id: json.id,
	promotionName: json.name ?? null,
	code: json.code ?? EMPTY_CODE_PLACEHOLDER,
	isInfluencer: Boolean(json.is_influencer),
	isB2b: Boolean(json.is_b2b),
	isBulk: json.coupons_count > 1,
	couponsCount: json.coupons_count || null,
	campaignId: json.campaign_id,
	campaignName: json.campaign?.name ?? null,
	discountType: json.discount_type as ENewDiscountType,
	discountTypeLabel: NewDiscountTypeMap[json.discount_type as ENewDiscountType] ?? json.discount_type_label,
	discountValueLabel: json.discount_type === ENewDiscountType.TIERED ? 'Progi rabatowe' : json.discount_value_label,
	workflowStatus: json.workflow_status,
	displayStatus: json.display_status,
	isActive: json.is_active,
	startsAt: json.starts_at ? new Date(json.starts_at) : null,
	endsAt: json.ends_at ? new Date(json.ends_at) : null,
	ownerName: employeeFullName(json.created_by),
	createdByEmployeeId: json.created_by?.id ?? null,
	requesterName: json.requester_name ?? null,
	createdAt: json.created_at ? new Date(json.created_at) : null,
})

export const CodeOptions = (raw: ICodeOptionsDto): ICodeOptionsModel => ({
	recipientSegments: raw.recipient_segments,
	offer: raw.offer,
	digitalGiftCards: raw.digital_gift_cards,
	gadgets: raw.gadgets.map(g => ({
		id: g.id,
		name: g.name,
		hasVariants: g.has_variants,
		quantityRemaining: g.quantity_remaining,
	})),
	campaigns: (raw.campaigns ?? []).map(c => ({ value: c.id, label: c.name })),
	tieredThresholdTypes: raw.tiered_threshold_types ?? [],
	tieredDiscountTypes: raw.tiered_discount_types ?? [],
})

export const RecipientSegmentClient = (
	json: IDiscountCodeRecipientSegmentClientDto,
): IDiscountCodeRecipientSegmentClientModel => ({
	id: json.client_id,
	clientId: json.client_id,
	email: json.email,
	firstName: json.first_name,
	lastName: json.last_name,
	clientSegment: json.client_segment,
})

// 'scheduled' to frontendowy status wyświetlania (active + przyszły starts_at).
// Przy tworzeniu i edycji backend akceptuje 'draft', 'paused' lub 'active'.
// Brak statusu = active (zachowanie domyślne backendu).
const buildStatusPayload = (status: ECodeStatus, isEditMode: boolean): ECodeStatus | undefined => {
	if (status === ECodeStatus.SCHEDULED) return isEditMode ? ECodeStatus.ACTIVE : undefined
	return status
}

const buildRecipientsPayload = (
	accessType: EAccessType,
	recipientSegment: string | null,
	selectedEmails: string[],
	allowedDomains: string[],
): IDiscountCodeRecipientsPayload => {
	if (accessType === EAccessType.SEGMENT && recipientSegment) {
		return { mode: 'segment', segment: recipientSegment }
	}
	if (accessType === EAccessType.SELECTED_EMAILS) {
		return { mode: 'selected_emails', emails: selectedEmails.map(email => ({ email, is_exist: true })) }
	}
	if (accessType === EAccessType.DOMAIN) {
		return { mode: 'selected_domains', domains: allowedDomains }
	}
	return { mode: 'all' }
}

const buildDiscountValue = (discountType: ENewDiscountType, discountValue: number | null): number | undefined => {
	if (discountValue === null) return undefined
	if (discountType === ENewDiscountType.FIXED) return PriceToPayload(discountValue) ?? undefined
	return discountValue
}

const buildCartAmountCondition = (form: IDiscountCodeFormData): Record<string, number> => {
	const cart: Record<string, number> = {}
	if (form.minOrderAmount !== null) cart.min = PriceToPayload(form.minOrderAmount) ?? 0
	if (form.maxOrderAmount !== null) cart.max = PriceToPayload(form.maxOrderAmount) ?? 0
	return cart
}

const buildOrderDaysCondition = (form: IDiscountCodeFormData): Record<string, unknown> => {
	const days: Record<string, unknown> = { enabled: true }
	if (form.minDays !== null) days.min = form.minDays
	if (form.maxDays !== null) days.max = form.maxDays
	return days
}

const buildConditionsPayload = (form: IDiscountCodeFormData): Record<string, unknown> | undefined => {
	const conditions: Record<string, unknown> = {}
	if (form.hasOrderAmountRestriction) conditions.cart_amount = buildCartAmountCondition(form)
	if (DISCOUNT_CODE_FLAGS.orderDaysThreshold && form.hasDaysRestriction) {
		conditions.order_days = buildOrderDaysCondition(form)
	}
	if (form.hasMealsPerDayRestriction) {
		const meals: Record<string, unknown> = { enabled: true }
		if (form.minMealsPerDay !== null) meals.min = form.minMealsPerDay
		if (form.maxMealsPerDay !== null) meals.max = form.maxMealsPerDay
		conditions.meals_per_day = meals
	}
	return Object.keys(conditions).length > 0 ? conditions : undefined
}

const SCOPE_GROUP_TARGET: Partial<Record<EProductScopeGroup, string>> = {
	[EProductScopeGroup.OFFERS]: 'offer',
	[EProductScopeGroup.CATEGORIES]: 'category',
	[EProductScopeGroup.MEALS]: 'meal',
	[EProductScopeGroup.TAGS]: 'tag',
}

/** Coerce a value that may be a string (from v-model) to number | null. */
const toIntOrNull = (v: unknown): number | null => {
	if (v === null || v === undefined || v === '') return null
	const n = Number(v)
	return Number.isNaN(n) ? null : n
}

const buildScopePayload = (
	productScope: EProductScope,
	productScopeGroups: IProductScopeGroups,
): { mode: string; filters: unknown[] } | undefined => {
	if (productScope === EProductScope.ALL) return undefined

	const filters = (Object.entries(productScopeGroups) as [EProductScopeGroup, IProductScopeGroup][])
		.filter(([, group]) => group.enabled && group.selectedIds.length > 0)
		.flatMap(([key, group]) => {
			const target = SCOPE_GROUP_TARGET[key]
			if (!target) return []
			const idKey = key === EProductScopeGroup.CATEGORIES ? 'values' : 'ids'
			const filter: Record<string, unknown> = { target, mode: group.mode, [idKey]: group.selectedIds }
			const minCount = toIntOrNull(group.minCount)
			const maxCount = toIntOrNull(group.maxCount)
			if (minCount !== null) filter.min_count = minCount
			if (maxCount !== null) filter.max_count = maxCount
			return [filter]
		})

	if (filters.length === 0) return undefined
	return { mode: 'filtered', filters }
}

const buildTieredReward = (d: IThresholdTierDiscount): ITieredRewardPayload => {
	const reward: ITieredRewardPayload = { discount_type: d.discountType }
	if (d.discountType === ENewDiscountType.GIFT) {
		if (d.giftType === EGiftType.GADGET && d.gadgetId !== null) reward.gadget_id = d.gadgetId
		if (d.giftType === EGiftType.DIGITAL_REWARD && d.digitalRewardId !== null)
			reward.digital_gift_card = d.digitalRewardId
	} else if (d.discountValue !== null) {
		reward.value =
			d.discountType === ENewDiscountType.FIXED ? (PriceToPayload(d.discountValue) ?? 0) : d.discountValue
	}
	return reward
}

const buildTieredPayload = (
	tiers: IThresholdTier[],
	productScopeGroups: IProductScopeGroups,
): IDiscountCodeCreatePayload['tiered'] => {
	if (tiers.length === 0) return undefined
	const uiType = tiers[0].conditionType
	const thresholdType = isItemCountType(uiType) ? EThresholdConditionType.ITEM_COUNT : uiType
	const payload: ITieredSectionPayload = {
		threshold_type: thresholdType,
		tiers: tiers.map(tier => ({
			min:
				thresholdType === EThresholdConditionType.CART_VALUE
					? (PriceToPayload(tier.conditionValue) ?? 0)
					: Number(tier.conditionValue) || 0,
			rewards: tier.discounts.map(buildTieredReward),
		})),
	}
	if (uiType === EThresholdConditionType.MEAL_COUNT) {
		payload.threshold_variant = 'meal'
	} else if (uiType === EThresholdConditionType.DIET_COUNT) {
		payload.threshold_variant = 'diet'
	}
	if (isItemCountType(uiType) && uiType !== EThresholdConditionType.ITEM_COUNT) {
		const scopeEntries: ITieredItemScopeEntryPayload[] = []
		const offersGroup = productScopeGroups[EProductScopeGroup.OFFERS]
		const mealsGroup = productScopeGroups[EProductScopeGroup.MEALS]
		if (offersGroup?.enabled && offersGroup.selectedIds.length > 0) {
			scopeEntries.push({ model: 'diet', ids: offersGroup.selectedIds as number[] })
		}
		if (mealsGroup?.enabled && mealsGroup.selectedIds.length > 0) {
			scopeEntries.push({ model: 'meal', ids: mealsGroup.selectedIds as number[] })
		}
		if (scopeEntries.length > 0) {
			payload.item_scope = scopeEntries
		}
	}
	return payload
}

const buildForFreePayload = (form: IDiscountCodeFormData): IDiscountCodeCreatePayload['for_free'] => {
	if (form.discountType !== ENewDiscountType.GIFT) return undefined
	if (form.giftType === EGiftType.GADGET && form.gadgetId !== null) {
		return { gadget_id: form.gadgetId }
	}
	if (form.giftType === EGiftType.DIGITAL_REWARD && form.digitalRewardId !== null) {
		return { digital_gift_card: form.digitalRewardId }
	}
	return undefined
}

const buildCodeSection = (form: IDiscountCodeFormData): IDiscountCodeCreatePayload['code'] => {
	if (form.codeGenerationType === ECodeGenerationType.RANDOM) return undefined
	return { mode: 'manual', value: form.code }
}

const buildBulkSection = (form: IDiscountCodeFormData): IDiscountCodeCreatePayload['bulk'] => {
	if (form.codeGenerationType !== ECodeGenerationType.RANDOM) return undefined
	const bulk: IDiscountCodeBulkPayload = {
		count: form.codeCount!,
		code_length: form.codeCharLen!,
		sequential: false,
		random_charset: form.codeCharType,
	}
	if (form.codePrefix) bulk.prefix = form.codePrefix
	return bulk
}

const buildGeneralSection = (
	form: IDiscountCodeFormData,
	isEditMode = false,
): IDiscountCodeCreatePayload['general'] => {
	const discountType = form.discountType!
	const general: IDiscountCodeCreatePayload['general'] = {
		name: form.promotionName,
		discount_type: discountType,
		business_description: form.description,
	}
	if (form.conditionsDisplay) general.rule_description = form.conditionsDisplay
	general.campaign_id = form.campaignId ?? null
	const discountVal = buildDiscountValue(discountType, form.discountValue)
	if (discountVal !== undefined) general.value = discountVal
	general.starts_at = form.startsAt ? format(form.startsAt, 'yyyy-MM-dd') : null
	general.ends_at = form.endsAt ? format(form.endsAt, 'yyyy-MM-dd') : null
	general.usage_limit = form.totalUsageLimit
	general.per_customer_usage_limit = form.usageLimitPerCustomer
	const status = buildStatusPayload(form.status ?? ECodeStatus.DRAFT, isEditMode)
	if (status !== undefined) general.status = status
	general.is_b2b = form.isB2b
	return general
}

const buildInfluencerSection = (form: IDiscountCodeFormData): IDiscountCodeCreatePayload['influencer'] => ({
	is_influencer: form.isInfluencer,
	influencer_name: form.isInfluencer ? (form.influencerName ?? null) : null,
	influencer_client_id: form.isInfluencer ? (form.influencerClientID ?? null) : null,
	commission: form.isInfluencer ? (form.commission ?? null) : null,
})

export const DiscountCodeCreatePayload = (
	form: IDiscountCodeFormData,
	state: {
		thresholdTiers: IThresholdTier[]
		recipientSegment: string | null
		selectedEmails: string[]
		allowedDomains: string[]
		requesterName: string | null
		isEditMode?: boolean
	},
): IDiscountCodeCreatePayload => {
	// Manualny kod powinien być możliwy do edycji przez PATCH,
	// więc sekcję `code` budujemy także w trybie edycji.
	const codeSection = buildCodeSection(form)
	const bulkSection = state.isEditMode ? undefined : buildBulkSection(form)
	const conditions = buildConditionsPayload(form)
	const scope = buildScopePayload(form.productScope, form.productScopeGroups)
	const forFree = buildForFreePayload(form)
	const tiered = buildTieredPayload(state.thresholdTiers, form.productScopeGroups)
	const general = buildGeneralSection(form, state.isEditMode)
	const influencer = buildInfluencerSection(form)

	return {
		...(codeSection ? { code: codeSection } : {}),
		...(bulkSection ? { bulk: bulkSection } : {}),
		general,
		influencer,
		limits: {
			recipients: buildRecipientsPayload(
				form.accessType,
				state.recipientSegment,
				state.selectedEmails,
				state.allowedDomains,
			),
		},
		...(state.requesterName ? { ownership: { requester_name: state.requesterName } } : {}),
		combining: {
			cashback_applies: form.canCombineWithCashback,
			cashback_override_percent: form.hasCashbackOverride ? (form.cashbackOverridePercent ?? null) : null,
			points_applies: form.canCombineWithGryzy,
		},
		// Przy edycji zawsze wysyłaj conditions/scope — pusty obiekt informuje backend
		// że ma usunąć istniejące rules (bez klucza backend pomija sync).
		conditions: conditions ?? {},
		scope: scope ?? { mode: 'all', filters: [] },
		...(forFree ? { for_free: forFree } : {}),
		...(tiered ? { tiered } : {}),
	}
}

const ACTION_TO_DISCOUNT_TYPE: Record<string, ENewDiscountType> = {
	cart_percentage_discount: ENewDiscountType.PERCENTAGE,
	cart_fixed_discount: ENewDiscountType.FIXED,
	cashback_override: ENewDiscountType.CASHBACK,
	free_delivery: ENewDiscountType.FREE_DELIVERY,
	loyalty_points_grant: ENewDiscountType.GRYZY,
	for_free_reward: ENewDiscountType.GIFT,
	tiered_percentage_discount: ENewDiscountType.TIERED,
}

const extractActionDiscountValue = (type: string, config: Record<string, unknown>): number | null => {
	switch (type) {
		case 'cart_percentage_discount':
			return (config.percent as number) ?? null
		case 'cart_fixed_discount':
			return config.amount !== undefined ? dividePrice(config.amount as number) : null
		case 'cashback_override':
			return (config.percent as number) ?? null
		case 'loyalty_points_grant':
			return (config.points as number) ?? null
		case 'free_diet_days':
			return (config.days_count as number) ?? null
		default:
			return null
	}
}

const ruleFirstConfig = (rule: IDiscountCodeRuleDto): Record<string, unknown> => {
	const c = rule.configuration
	if (Array.isArray(c) && c.length > 0 && typeof c[0] === 'object' && c[0] !== null) {
		return c[0] as Record<string, unknown>
	}
	return (c ?? {}) as Record<string, unknown>
}

const extractCoupons = (dto: IDiscountCodeDetailDto) => {
	let coupons = dto.coupons ?? []
	if (!coupons.length && dto.coupon) coupons = [dto.coupon]
	const isBulk = coupons.length > 1
	const primaryCoupon = dto.coupon ?? coupons[0] ?? null
	return { isBulk, primaryCoupon, coupons }
}

const ruleAmount = (rule: IDiscountCodeRuleDto | undefined): number | null => {
	if (!rule) return null
	const val = ruleFirstConfig(rule).amount as number | undefined
	return val !== undefined ? dividePrice(val) : null
}

const ruleFlatNum = (cfg: Record<string, unknown> | null, key: string): number | null => {
	if (!cfg) return null
	const val = cfg[key] as number | null | undefined
	return val ?? null
}

const ruleFlatCfg = (rule: IDiscountCodeRuleDto | undefined): Record<string, unknown> | null =>
	rule ? (rule.configuration as Record<string, unknown>) : null

const extractConditionsFromRules = (rules: IDiscountCodeRuleDto[]) => {
	const byType = (t: string) => rules.find(r => r.type === t)

	const minAmtRule = byType('cart_minimum_value')
	const maxAmtRule = byType('cart_maximum_value')
	const daysRule = byType('order_days_threshold')
	const mealsRule = byType('meals_per_day_threshold')
	const daysCfg = ruleFlatCfg(daysRule)
	const mealsCfg = ruleFlatCfg(mealsRule)

	const hasOrderAmountRestriction = Boolean(minAmtRule || maxAmtRule)
	const hasDaysRestriction = Boolean(daysRule)
	const hasMealsPerDayRestriction = Boolean(mealsRule)
	const minOrderAmount = ruleAmount(minAmtRule)
	const maxOrderAmount = ruleAmount(maxAmtRule)
	const minDays = ruleFlatNum(daysCfg, 'min')
	const maxDays = ruleFlatNum(daysCfg, 'max')
	const maxMealsPerDay = ruleFlatNum(mealsCfg, 'max')
	const minMealsPerDay = ruleFlatNum(mealsCfg, 'min')

	return {
		hasOrderAmountRestriction,
		minOrderAmount,
		maxOrderAmount,
		hasDaysRestriction,
		minDays,
		maxDays,
		hasMealsPerDayRestriction,
		minMealsPerDay,
		maxMealsPerDay,
	}
}

interface IAccessResult {
	accessType: EAccessType
	selectedEmails: string[]
	allowedDomains: string[]
	recipientSegment: string | null
}

const extractAccessFromRules = (rules: IDiscountCodeRuleDto[]): IAccessResult => {
	const byType = (t: string) => rules.find(r => r.type === t)
	const clientsRule = byType('selected_clients')
	const domainsRule = byType('selected_domains')
	const firstOrderRule = byType('first_order')
	const returningCustomerRule = byType('returning_customer')
	const clientSegmentRule = byType('client_segment')

	if (clientsRule) {
		const recipients = (ruleFirstConfig(clientsRule).recipients as { email: string }[]) ?? []
		return {
			accessType: EAccessType.SELECTED_EMAILS,
			selectedEmails: recipients.map(r => r.email),
			allowedDomains: [],
			recipientSegment: null,
		}
	}
	if (domainsRule) {
		const domains = (ruleFirstConfig(domainsRule).domains as string[]) ?? []
		return { accessType: EAccessType.DOMAIN, selectedEmails: [], allowedDomains: domains, recipientSegment: null }
	}
	if (firstOrderRule) {
		return {
			accessType: EAccessType.SEGMENT,
			selectedEmails: [],
			allowedDomains: [],
			recipientSegment: 'first_order',
		}
	}
	if (returningCustomerRule) {
		return {
			accessType: EAccessType.SEGMENT,
			selectedEmails: [],
			allowedDomains: [],
			recipientSegment: 'returning_customer',
		}
	}
	if (clientSegmentRule) {
		const segments = (ruleFirstConfig(clientSegmentRule).segments as string[]) ?? []
		return {
			accessType: EAccessType.SEGMENT,
			selectedEmails: [],
			allowedDomains: [],
			recipientSegment: segments[0] ?? null,
		}
	}
	return { accessType: EAccessType.ALL, selectedEmails: [], allowedDomains: [], recipientSegment: null }
}

const resolveGiftType = (r: Record<string, unknown>): EGiftType | null => {
	if (r.digital_gift_card) return EGiftType.DIGITAL_REWARD
	if (r.gadget_id) return EGiftType.GADGET
	return null
}

const findPrimaryAction = (actions: IDiscountCodeActionDto[] | undefined): IDiscountCodeActionDto | undefined => {
	if (!actions?.length) return undefined
	if (actions.length > 1) return actions.find(a => a.type !== 'cashback_override') ?? actions[0]
	return actions[0]
}

const buildDiscountInfo = (actions: IDiscountCodeActionDto[] | undefined) => {
	const action = findPrimaryAction(actions)
	const discountType = action ? (ACTION_TO_DISCOUNT_TYPE[action.type] ?? null) : null
	const discountValue = action ? extractActionDiscountValue(action.type, action.configuration) : null
	const config = (action?.configuration ?? {}) as Record<string, unknown>
	const giftType = discountType === ENewDiscountType.GIFT ? resolveGiftType(config) : null
	const gadgetId = discountType === ENewDiscountType.GIFT ? ((config.gadget_id as number) ?? null) : null
	const digitalRewardId =
		discountType === ENewDiscountType.GIFT ? ((config.digital_gift_card as string) ?? null) : null
	return { discountType, discountValue, giftType, gadgetId, digitalRewardId }
}

const buildDatesAndMeta = (dto: IDiscountCodeDetailDto, primaryCoupon: IDiscountCodeCouponDto | null) => {
	const startsAt = dto.starts_at ? new Date(dto.starts_at) : null
	const endsAt = dto.ends_at ? new Date(dto.ends_at) : null
	const totalUsageLimit = primaryCoupon?.usage_limit ?? null
	const usageLimitPerCustomer = primaryCoupon?.per_customer_usage_limit ?? null
	const status = dto.workflow_status ?? null
	const canCombineWithCashback = dto.cashback_applies ?? true
	const cashbackAction = dto.actions?.find(a => a.type === 'cashback_override')
	const cashbackOverridePercent = cashbackAction ? ((cashbackAction.configuration.percent as number) ?? null) : null
	const hasCashbackOverride = Boolean(cashbackAction)
	const canCombineWithGryzy = dto.points_applies ?? true
	return {
		startsAt,
		endsAt,
		totalUsageLimit,
		usageLimitPerCustomer,
		status,
		hasCashbackOverride,
		canCombineWithCashback,
		cashbackOverridePercent,
		canCombineWithGryzy,
	}
}

const buildCodeInfo = (
	isBulk: boolean,
	primaryCoupon: IDiscountCodeCouponDto | null,
	coupons: IDiscountCodeCouponDto[],
	promotionName: string | null,
) => {
	const inferBulkConfig = (): { codePrefix: string; codeCharLen: number | null; codeCharType: ECodeCharType } => {
		const nonEmptyCodes = coupons.map(c => c.code ?? '').filter(Boolean)
		if (!nonEmptyCodes.length) return { codePrefix: '', codeCharLen: null, codeCharType: ECodeCharType.MIXED }

		const namePrefix = promotionName?.endsWith(BULK_SUFFIX)
			? promotionName.slice(0, -BULK_SUFFIX.length).trim()
			: ''

		const longestCommonPrefix = nonEmptyCodes.reduce((prefix, code) => {
			let i = 0
			while (i < prefix.length && i < code.length && prefix[i] === code[i]) i += 1
			return prefix.slice(0, i)
		})

		const inferCharset = (parts: string[]): ECodeCharType | null => {
			if (parts.every(part => DIGITS_ONLY_REGEX.test(part))) return ECodeCharType.DIGITS_ONLY
			if (parts.every(part => LETTERS_ONLY_REGEX.test(part))) return ECodeCharType.LETTERS_ONLY
			if (parts.every(part => ALPHANUMERIC_REGEX.test(part))) return ECodeCharType.MIXED
			return null
		}

		const tryPrefix = (
			prefix: string,
		): { codePrefix: string; codeCharLen: number | null; codeCharType: ECodeCharType } => {
			const randomParts = nonEmptyCodes.map(code => code.slice(prefix.length))
			const lengths = [...new Set(randomParts.map(part => part.length))]
			const codeCharLen = lengths.length === 1 ? lengths[0] : null
			const codeCharType = inferCharset(randomParts) ?? ECodeCharType.MIXED
			const isAllowedLength =
				codeCharLen !== null && codeCharLen >= BULK_CODE_CHAR_LEN_MIN && codeCharLen <= BULK_CODE_CHAR_LEN_MAX
			const isPrefixValid = randomParts.every(part => part.length > 0)
			if (isAllowedLength && isPrefixValid) return { codePrefix: prefix, codeCharLen, codeCharType }
			return { codePrefix: prefix, codeCharLen: null, codeCharType }
		}

		const prefixCandidates = [namePrefix, longestCommonPrefix, '']
			.filter((value, index, arr) => arr.indexOf(value) === index)
			.filter(prefix => nonEmptyCodes.every(code => code.startsWith(prefix)))

		for (const prefix of prefixCandidates) {
			const inferred = tryPrefix(prefix)
			if (inferred.codeCharLen !== null) return inferred
		}

		return tryPrefix(prefixCandidates[0] ?? '')
	}

	const codeGenerationType = isBulk ? ECodeGenerationType.RANDOM : ECodeGenerationType.SIMPLE
	const code = !isBulk && primaryCoupon?.code ? primaryCoupon.code : ''
	const codeCount = isBulk ? coupons.length : null
	if (!isBulk) {
		return {
			codeGenerationType,
			code,
			codeCount,
			codePrefix: '',
			codeCharLen: null,
			codeCharType: ECodeCharType.MIXED,
		}
	}

	return { codeGenerationType, code, codeCount, ...inferBulkConfig() }
}

const extractRewardValue = (reward: Record<string, unknown>): number | null => {
	if (reward.percent !== undefined) return reward.percent as number
	if (reward.value !== undefined) return reward.value as number
	if (reward.amount !== undefined) return dividePrice(reward.amount as number) ?? 0
	if (reward.points !== undefined) return reward.points as number
	if (reward.days_count !== undefined) return reward.days_count as number
	return null
}

interface IStoredItemScopeEntry {
	model: string
	ids?: number[]
	except_ids?: number[]
	columns?: { column: string; values: unknown[] }[]
}

const makeScopeGroup = (
	selectedIds: (number | string)[],
	except_ids?: number[],
	minCount: number | null = null,
	maxCount: number | null = null,
): IProductScopeGroup => ({
	enabled: true,
	mode: except_ids?.length ? 'exclude' : 'include',
	selectedIds,
	minCount,
	maxCount,
})

/** Match an item_threshold rule to a scope entry by model + ids/columns signature. */
const matchesThresholdRule = (
	cfg: Record<string, unknown>,
	model: string,
	ids?: number[],
	columns?: { column: string; values: unknown[] }[],
): boolean => {
	if (cfg.model !== model) return false
	if (ids?.length) {
		const ruleIds = cfg.ids as number[] | undefined
		return Boolean(ruleIds) && ruleIds!.length === ids.length && ruleIds!.every(id => ids.includes(id))
	}
	if (columns?.length) {
		const ruleCols = cfg.columns as { column: string; values: unknown[] }[] | undefined
		return Boolean(ruleCols) && columns.every(col => ruleCols!.some(rc => rc.column === col.column))
	}
	return true
}

const findThresholdRule = (
	rules: IDiscountCodeRuleDto[],
	model: string,
	ids?: number[],
	columns?: { column: string; values: unknown[] }[],
) => rules.find(rule => matchesThresholdRule(rule.configuration as Record<string, unknown>, model, ids, columns))

const applyScopeEntry = (
	groups: IProductScopeGroups,
	entry: IStoredItemScopeEntry,
	minCount: number | null,
	maxCount: number | null,
) => {
	const { model, ids, except_ids, columns } = entry
	if (columns?.length) {
		const subkindCol = columns.find(c => c.column === 'meal_subkind')
		const tagCol = columns.find(c => c.column === 'filter_tag_ids')
		if (subkindCol)
			groups[EProductScopeGroup.CATEGORIES] = makeScopeGroup(
				subkindCol.values as string[],
				undefined,
				minCount,
				maxCount,
			)
		else if (tagCol)
			groups[EProductScopeGroup.TAGS] = makeScopeGroup(tagCol.values as number[], undefined, minCount, maxCount)
	} else if (model === 'diet') {
		groups[EProductScopeGroup.OFFERS] = makeScopeGroup(except_ids ?? ids ?? [], except_ids, minCount, maxCount)
	} else if (model === 'meal') {
		groups[EProductScopeGroup.MEALS] = makeScopeGroup(except_ids ?? ids ?? [], except_ids, minCount, maxCount)
	}
}

const parseScopeEntries = (
	entries: IStoredItemScopeEntry[],
	itemThresholdRules: IDiscountCodeRuleDto[] = [],
): IProductScopeGroups | null => {
	if (!entries.length) return null

	const groups: IProductScopeGroups = {
		[EProductScopeGroup.OFFERS]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
		[EProductScopeGroup.CATEGORIES]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
		[EProductScopeGroup.MEALS]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
		[EProductScopeGroup.TAGS]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
	}

	for (const entry of entries) {
		const threshold = findThresholdRule(
			itemThresholdRules,
			entry.model,
			entry.ids ?? entry.except_ids,
			entry.columns,
		)
		const thresholdCfg = ruleFlatCfg(threshold)
		const minCount = ruleFlatNum(thresholdCfg, 'min_count')
		const maxCount = ruleFlatNum(thresholdCfg, 'max_count')
		applyScopeEntry(groups, entry, minCount, maxCount)
	}

	const hasAnySelection = Object.values(groups).some(g => g.enabled && g.selectedIds.length > 0)
	return hasAnySelection ? groups : null
}

const extractScopeGroupsFromTieredItemScope = (
	actions: IDiscountCodeActionDto[],
	rules: IDiscountCodeRuleDto[],
): IProductScopeGroups | null => {
	const tieredAction = actions.find(a => a.type === 'tiered_percentage_discount')
	if (!tieredAction) return null

	const config = tieredAction.configuration as {
		item_scope?: IStoredItemScopeEntry[]
	}
	const entries = config.item_scope
	if (!entries?.length) return null

	const itemThresholdRules = rules.filter(r => r.type === 'item_threshold')
	return parseScopeEntries(entries, itemThresholdRules)
}

const extractScopeGroupsFromDietaryProductRule = (rules: IDiscountCodeRuleDto[]): IProductScopeGroups | null => {
	const rule = rules.find(r => r.type === 'dietary_product')
	if (!rule) return null

	const entries = rule.configuration as IStoredItemScopeEntry[]
	if (!Array.isArray(entries)) return null

	const itemThresholdRules = rules.filter(r => r.type === 'item_threshold')
	return parseScopeEntries(entries, itemThresholdRules)
}

const resolveThresholdConditionType = (raw: EThresholdConditionType, variant?: string): EThresholdConditionType => {
	if (raw !== EThresholdConditionType.ITEM_COUNT) return raw
	if (variant === 'meal') return EThresholdConditionType.MEAL_COUNT
	if (variant === 'diet') return EThresholdConditionType.DIET_COUNT
	return EThresholdConditionType.ITEM_COUNT
}

const extractTieredTiers = (actions: IDiscountCodeActionDto[]): IThresholdTier[] => {
	const tieredAction = actions.find(a => a.type === 'tiered_percentage_discount')
	if (!tieredAction) return []
	const config = tieredAction.configuration as {
		threshold_type: string
		threshold_variant?: string
		tiers: { min: number; rewards: Record<string, unknown>[] }[]
	}
	if (!config?.tiers?.length) return []
	const rawThresholdType = (config.threshold_type ?? EThresholdConditionType.CART_VALUE) as EThresholdConditionType
	const thresholdType: EThresholdConditionType = resolveThresholdConditionType(
		rawThresholdType,
		config.threshold_variant,
	)
	const tiers = config.tiers.map(tier => ({
		id: crypto.randomUUID(),
		conditionType: thresholdType,
		conditionValue:
			thresholdType === EThresholdConditionType.CART_VALUE ? Math.round(dividePrice(tier.min) ?? 0) : tier.min,
		discounts: (tier.rewards ?? []).map(r => ({
			discountType: (r.discount_type as ENewDiscountType) ?? ENewDiscountType.PERCENTAGE,
			discountValue: extractRewardValue(r),
			giftType: resolveGiftType(r),
			gadgetId: (r.gadget_id as number) ?? null,
			digitalRewardId: (r.digital_gift_card as string) ?? null,
		})),
	}))
	// Propagate GIFT sub-values from previous tiers where they may not have been stored
	for (let i = 1; i < tiers.length; i++) {
		tiers[i].discounts.forEach((discount, di) => {
			if (discount.discountType === ENewDiscountType.GIFT && discount.giftType === null) {
				const source = tiers[i - 1].discounts[di]
				if (source?.discountType === ENewDiscountType.GIFT) {
					discount.giftType = source.giftType
					discount.gadgetId = source.gadgetId
					discount.digitalRewardId = source.digitalRewardId
				}
			}
		})
	}
	return tiers
}

const buildDefaultScopeGroups = (): IProductScopeGroups => ({
	[EProductScopeGroup.OFFERS]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
	[EProductScopeGroup.CATEGORIES]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
	[EProductScopeGroup.MEALS]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
	[EProductScopeGroup.TAGS]: { ...DEFAULT_PRODUCT_SCOPE_GROUP },
})

const resolveScopeData = (
	thresholdTiers: IThresholdTier[],
	actions: IDiscountCodeActionDto[],
	rules: IDiscountCodeRuleDto[],
): { productScope: EProductScope; productScopeGroups: IProductScopeGroups } => {
	const isItemCount = isItemCountType(thresholdTiers[0]?.conditionType)
	// Reguła dietary_product zawsze zawiera pełny zakres (diety + kategorie + posiłki + tagi).
	// Dla item_count jako fallback używamy item_scope akcji (legacy kody bez reguły dietary_product).
	const extractedScopeGroups =
		extractScopeGroupsFromDietaryProductRule(rules) ??
		(isItemCount ? extractScopeGroupsFromTieredItemScope(actions, rules) : null)
	return {
		productScope: extractedScopeGroups ? EProductScope.SELECTED : EProductScope.ALL,
		productScopeGroups: extractedScopeGroups ?? buildDefaultScopeGroups(),
	}
}

const buildInfluencerInitialValues = (
	dto: IDiscountCodeDetailDto,
): Pick<IDiscountCodeFormData, 'isInfluencer' | 'influencerName' | 'influencerClientID' | 'commission'> => ({
	isInfluencer: dto.is_influencer ?? false,
	influencerName: dto.influencer_name ?? null,
	influencerClientID: dto.influencer_client_id ?? null,
	commission: dto.commission ?? null,
})

export const DiscountCodeDetailToInitialValues = (dto: IDiscountCodeDetailDto): IDiscountCodeFormData => {
	const { isBulk, primaryCoupon, coupons } = extractCoupons(dto)
	const { codeGenerationType, code, codeCount, codePrefix, codeCharLen, codeCharType } = buildCodeInfo(
		isBulk,
		primaryCoupon,
		coupons,
		dto.name,
	)
	const { discountType, discountValue, giftType, gadgetId, digitalRewardId } = buildDiscountInfo(dto.actions)
	const {
		startsAt,
		endsAt,
		totalUsageLimit,
		usageLimitPerCustomer,
		status,
		hasCashbackOverride,
		canCombineWithCashback,
		cashbackOverridePercent,
		canCombineWithGryzy,
	} = buildDatesAndMeta(dto, primaryCoupon)
	const rules = dto.rules ?? []
	const conditions = extractConditionsFromRules(rules)
	const access = extractAccessFromRules(rules)
	const actions = dto.actions ?? []
	const thresholdTiers = extractTieredTiers(actions)
	const scopeData = resolveScopeData(thresholdTiers, actions, rules)

	return {
		promotionName: dto.name ?? '',
		campaignId: dto.campaign_id ?? null,
		...buildInfluencerInitialValues(dto),
		isB2b: dto.is_b2b ?? false,
		codePrefix,
		codeCharLen,
		codeCharType,
		description: dto.business_description ?? '',
		giftType,
		gadgetId,
		digitalRewardId,
		conditionsDisplay: dto.rule_description ?? null,
		thresholdTiers,
		productScope: scopeData.productScope,
		productScopeGroups: scopeData.productScopeGroups,
		requesterName: dto.requester_name ?? null,
		codeGenerationType,
		code,
		codeCount,
		startsAt,
		endsAt,
		totalUsageLimit,
		usageLimitPerCustomer,
		status,
		hasCashbackOverride,
		canCombineWithCashback,
		cashbackOverridePercent,
		canCombineWithGryzy,
		discountType,
		discountValue,
		...conditions,
		...access,
	}
}
